import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ReferenciasService } from '../services';
import { useEmpresasContext } from '../context/EmpresasContext';
import type {
  BaseFilter,
  FaenaFilter,
  PredioFilter,
  RodalFilter,
  InsumoFilter,
  ItemChequeoFilter,
  ProveedorFilter,
  VehiculoFilter,
  TarjetaFilter,
  EmisorFilter,
  CrearOperador,
  CrearFaena,
  CrearPredio,
  CrearRodal,
  CrearInsumo,
  CrearItemChequeo,
  CrearProveedor,
  CrearVehiculo,
  CrearTarjeta,
  CrearEmisor
} from '../services/referenciasService';
// Tipos utilizados en los hooks de referencias
import type {
  Operador,
  Faena,
  Predio,
  Rodal,
  Insumo,
  ItemChequeo,
  Proveedor,
  Vehiculo,
  Tarjeta,
  Emisor,
  PaginatedResponse
} from '../types';

// ===============================
// HOOKS PARA OPERADORES
// ===============================

export const useOperadores = (filtros: BaseFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Operador>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['operadores', empresaId, filtros],
    () => ReferenciasService.obtenerOperadores(filtros),
    {
      enabled: !!empresaId, // Solo ejecutar si hay empresa activa
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutos
    }
  );
};

export const useOperador = (id: number): ReturnType<typeof useQuery<Operador>> => {
  return useQuery(
    ['operador', id],
    () => ReferenciasService.obtenerOperador(id),
    {
      enabled: !!id,
    }
  );
};

export const useCrearOperador = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (datos: CrearOperador) => ReferenciasService.crearOperador(datos),
    {
      onSuccess: () => {
        // Invalidar todas las queries de operadores para la empresa actual
        queryClient.invalidateQueries(['operadores', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarOperador = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearOperador> }) =>
      ReferenciasService.actualizarOperador(id, datos),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['operadores', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['operador', id]);
      },
    }
  );
};

export const useEliminarOperador = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (id: number) => ReferenciasService.eliminarOperador(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['operadores', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOKS PARA FAENAS
// ===============================

export const useFaenas = (filtros: FaenaFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Faena>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['faenas', empresaId, filtros],
    () => ReferenciasService.obtenerFaenas(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useFaena = (id: number) => {
  return useQuery(
    ['faena', id],
    () => ReferenciasService.obtenerFaena(id),
    {
      enabled: !!id,
    }
  );
};

export const useCrearFaena = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (datos: CrearFaena) => ReferenciasService.crearFaena(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['faenas', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarFaena = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearFaena> }) =>
      ReferenciasService.actualizarFaena(id, datos),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['faenas', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['faena', id]);
      },
    }
  );
};

export const useEliminarFaena = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (id: number) => ReferenciasService.eliminarFaena(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['faenas', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOKS PARA PREDIOS
// ===============================

export const usePredios = (filtros: PredioFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Predio>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['predios', empresaId, filtros],
    () => ReferenciasService.obtenerPredios(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const usePredio = (id: number) => {
  return useQuery(
    ['predio', id],
    () => ReferenciasService.obtenerPredio(id),
    {
      enabled: !!id,
    }
  );
};

export const useCrearPredio = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (datos: CrearPredio) => ReferenciasService.crearPredio(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['predios', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarPredio = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearPredio> }) =>
      ReferenciasService.actualizarPredio(id, datos),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['predios', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['predio', id]);
      },
    }
  );
};

export const useEliminarPredio = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (id: number) => ReferenciasService.eliminarPredio(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['predios', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOKS PARA RODALES
// ===============================

export const useRodales = (filtros: RodalFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Rodal>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['rodales', empresaId, filtros],
    () => ReferenciasService.obtenerRodales(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useRodal = (id: number) => {
  return useQuery(
    ['rodal', id],
    () => ReferenciasService.obtenerRodal(id),
    {
      enabled: !!id,
    }
  );
};

export const useCrearRodal = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (datos: CrearRodal) => ReferenciasService.crearRodal(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rodales', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarRodal = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearRodal> }) =>
      ReferenciasService.actualizarRodal(id, datos),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['rodales', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['rodal', id]);
      },
    }
  );
};

export const useEliminarRodal = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (id: number) => ReferenciasService.eliminarRodal(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rodales', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOKS PARA INSUMOS
// ===============================

export const useInsumos = (filtros: InsumoFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Insumo>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['insumos', empresaId, filtros],
    () => ReferenciasService.obtenerInsumos(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useInsumo = (id: number) => {
  return useQuery(
    ['insumo', id],
    () => ReferenciasService.obtenerInsumo(id),
    {
      enabled: !!id,
    }
  );
};

export const useCrearInsumo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (datos: CrearInsumo) => ReferenciasService.crearInsumo(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['insumos', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarInsumo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearInsumo> }) =>
      ReferenciasService.actualizarInsumo(id, datos),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['insumos', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['insumo', id]);
      },
    }
  );
};

export const useEliminarInsumo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (id: number) => ReferenciasService.eliminarInsumo(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['insumos', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOKS PARA PROVEEDORES
// ===============================

export const useProveedores = (filtros: ProveedorFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Proveedor>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['proveedores', empresaId, filtros],
    () => ReferenciasService.obtenerProveedores(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCrearProveedor = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (datos: CrearProveedor) => ReferenciasService.crearProveedor(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['proveedores', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-proveedores', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarProveedor = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearProveedor> }) =>
      ReferenciasService.actualizarProveedor(id, datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['proveedores', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-proveedores', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useEliminarProveedor = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (id: number) => ReferenciasService.eliminarProveedor(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['proveedores', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-proveedores', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useListaProveedores = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['lista-proveedores', empresaId],
    () => ReferenciasService.obtenerListaProveedores(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

// ===============================
// HOOKS PARA VEHICULOS
// ===============================

export const useVehiculos = (filtros: VehiculoFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Vehiculo>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['vehiculos', empresaId, filtros],
    () => ReferenciasService.obtenerVehiculos(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCrearVehiculo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (datos: CrearVehiculo) => ReferenciasService.crearVehiculo(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vehiculos', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-vehiculos', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarVehiculo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearVehiculo> }) =>
      ReferenciasService.actualizarVehiculo(id, datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vehiculos', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-vehiculos', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useEliminarVehiculo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (id: number) => ReferenciasService.eliminarVehiculo(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vehiculos', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-vehiculos', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useListaVehiculos = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['lista-vehiculos', empresaId],
    () => ReferenciasService.obtenerListaVehiculos(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

// ===============================
// HOOKS PARA ITEMS DE CHEQUEO
// ===============================

export const useItemsChequeo = (filtros: ItemChequeoFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<ItemChequeo>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['items-chequeo', empresaId, filtros],
    () => ReferenciasService.obtenerItemsChequeo(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useItemChequeo = (id: number) => {
  return useQuery(
    ['item-chequeo', id],
    () => ReferenciasService.obtenerItemChequeo(id),
    {
      enabled: !!id,
    }
  );
};

export const useCrearItemChequeo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (datos: CrearItemChequeo) => ReferenciasService.crearItemChequeo(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['items-chequeo', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarItemChequeo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearItemChequeo> }) =>
      ReferenciasService.actualizarItemChequeo(id, datos),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['items-chequeo', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['item-chequeo', id]);
      },
    }
  );
};

export const useEliminarItemChequeo = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();
  
  return useMutation(
    (id: number) => ReferenciasService.eliminarItemChequeo(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['items-chequeo', empresaActiva?.empresa.id]);
      },
    }
  );
};

// ===============================
// HOOKS PARA LISTAS AUXILIARES
// ===============================

export const useListaOperadores = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['lista-operadores', empresaId],
    () => ReferenciasService.obtenerListaOperadores(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000, // 10 minutos
    }
  );
};

export const useListaFaenas = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['lista-faenas', empresaId],
    () => ReferenciasService.obtenerListaFaenas(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const useListaPredios = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['lista-predios', empresaId],
    () => ReferenciasService.obtenerListaPredios(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const useListaInsumos = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['lista-insumos', empresaId],
    () => ReferenciasService.obtenerListaInsumos(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const useListaItemsChequeo = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;
  
  return useQuery(
    ['lista-items-chequeo', empresaId],
    () => ReferenciasService.obtenerListaItemsChequeo(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

// 🆕 NUEVO: Hook para lista de máquinas
export const useListaMaquinas = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['lista-maquinas', empresaId],
    () => ReferenciasService.obtenerListaMaquinas(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

// ===============================
// HOOKS PARA TARJETAS
// ===============================

export const useTarjetas = (filtros: TarjetaFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Tarjeta>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['tarjetas', empresaId, filtros],
    () => ReferenciasService.obtenerTarjetas(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCrearTarjeta = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (datos: CrearTarjeta) => ReferenciasService.crearTarjeta(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tarjetas', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-tarjetas', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarTarjeta = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearTarjeta> }) =>
      ReferenciasService.actualizarTarjeta(id, datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tarjetas', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-tarjetas', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useEliminarTarjeta = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (id: number) => ReferenciasService.eliminarTarjeta(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tarjetas', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-tarjetas', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useListaTarjetas = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['lista-tarjetas', empresaId],
    () => ReferenciasService.obtenerListaTarjetas(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};

// ===============================
// HOOKS PARA EMISORES
// ===============================

export const useEmisores = (filtros: EmisorFilter = {}): ReturnType<typeof useQuery<PaginatedResponse<Emisor>>> => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['emisores', empresaId, filtros],
    () => ReferenciasService.obtenerEmisores(filtros),
    {
      enabled: !!empresaId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCrearEmisor = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (datos: CrearEmisor) => ReferenciasService.crearEmisor(datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['emisores', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-emisores', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useActualizarEmisor = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    ({ id, datos }: { id: number; datos: Partial<CrearEmisor> }) =>
      ReferenciasService.actualizarEmisor(id, datos),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['emisores', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-emisores', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useEliminarEmisor = () => {
  const queryClient = useQueryClient();
  const { empresaActiva } = useEmpresasContext();

  return useMutation(
    (id: number) => ReferenciasService.eliminarEmisor(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['emisores', empresaActiva?.empresa.id]);
        queryClient.invalidateQueries(['lista-emisores', empresaActiva?.empresa.id]);
      },
    }
  );
};

export const useListaEmisores = () => {
  const { empresaActiva } = useEmpresasContext();
  const empresaId = empresaActiva?.empresa.id;

  return useQuery(
    ['lista-emisores', empresaId],
    () => ReferenciasService.obtenerListaEmisores(),
    {
      enabled: !!empresaId,
      staleTime: 10 * 60 * 1000,
    }
  );
};
